import type { Application } from '@hotwired/stimulus';
import { Controller } from '@hotwired/stimulus';

export class InlineEditController extends Controller {
    static targets = ['content', 'renderedContent', 'toggle'];
    static classes = ['initalized', 'expanded'];

    /**
     * Register this controller in the application
     */
    static register(application: Application) {
        application.register('inline-edit', this);
    }

    /**
     * Selector for the elements to look for when checking for error states in the inline edit section
     */
    private static readonly FORM_FIELD_ERROR_MESSAGE_TARGET_SELECTOR = '[data-form-fields-target="errorMessage"]';

    /**
     * Element containing the expanded content
     */
    get contentElement(): HTMLElement {
        // @ts-ignore
        return this.hasContentTarget ? this.contentTarget : null;
    }

    /**
     * Element containing the collapsed content
     */
    get renderedContentElement(): HTMLElement {
        // @ts-ignore
        return this.hasRenderedContentTarget ? this.renderedContentTarget : null;
    }

    /**
     * Returns all elements in the inline edit wrapper that are considered error elements
     */
    get errorElements(): NodeList {
        return this.element.querySelectorAll(InlineEditController.FORM_FIELD_ERROR_MESSAGE_TARGET_SELECTOR);
    }

    /**
     * General Toggle Element
     */
    get toggleElement(): HTMLButtonElement {
        // @ts-ignore
        return this.hasToggleTarget ? this.toggleTarget : null;
    }

    /**
     * Returns true if there are errors in the inline edit
     */
    get containsFieldErrors(): boolean {
        return this.errorElements.length > 0;
    }

    /**
     * Class for when the element is initalized
     */
    get initalizedCSSClass(): string {
        // @ts-ignore
        return this.initalizedClass;
    }

    /**
     * Class for when the element is expanded
     */
    get expandedCSSClass(): string {
        // @ts-ignore
        return this.expandedClass;
    }

    connect(): void {
        this.element.classList.add(this.initalizedCSSClass);
        this.toggleElement.hidden = false;

        if (this.containsFieldErrors) {
            this.expand();
        } else {
            this.collapse();
        }
    }

    /**
     * Collapse/Expand element
     */
    toggle(e?: Event) {
        if (this.toggleElement.ariaExpanded === 'true') {
            this.collapse();
        } else {
            this.expand();
        }
    }

    expand(e?: Event) {
        this.toggleElement.ariaExpanded = 'true';
        this.element.classList.add(this.expandedCSSClass);
    }

    collapse(e?: Event) {
        this.toggleElement.ariaExpanded = 'false';
        this.element.classList.remove(this.expandedCSSClass);
        this.toggleElement.focus();
    }
}

export default InlineEditController;
